import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Books, ArrowsClockwise, SquaresFour, CreditCard, SignOut, Coins, Sun, Moon, UsersThree, Question, Sparkle, Lightning, Eye, Gear, Bell, BookOpen } from '@phosphor-icons/react';
import { GitCompareArrows } from 'lucide-react';
import axios from 'axios';
import { useAuthStore, billingAPI, notificationsAPI } from '../store/authStore';
import { useTheme } from '../context/ThemeContext';
import { LuminexLockup } from './LuminexLogo';
import WelcomeChatOverlay from './WelcomeChatOverlay';
import WalkthroughTour from './WalkthroughTour';
import GlobalSearchBar from './GlobalSearchBar';
import { useCompareStore } from '../store/compareStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const t = localStorage.getItem('capitalops_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function Layout() {
  // Selective Zustand subscriptions so Layout (which wraps every authed page)
  // only re-renders when user actually changes — not on every auth-store write.
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const refreshUser = useAuthStore(s => s.refreshUser);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Welcome chat: every login (skip persists only for this session)
  const [showWelcome, setShowWelcome] = useState(() => {
    return sessionStorage.getItem('vidhana_welcome_dismissed') !== '1';
  });
  const [showTour, setShowTour] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  useEffect(() => {
    if (!showProfileMenu) return;
    const onClick = (e) => { if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setShowProfileMenu(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showProfileMenu]);
  const profileItemStyle = { display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 6, color: 'var(--text-1)', fontSize: 12.5, fontWeight: 600 };

  // ───── Notifications dropdown ─────
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifMenuRef = useRef(null);
  const fetchNotifs = React.useCallback(() => {
    if (!user) return;
    notificationsAPI.list().then(r => {
      setNotifs(r.data?.items || []);
      setUnreadCount(r.data?.unreadCount || 0);
    }).catch(() => {});
  }, [user]);
  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);
  useEffect(() => {
    if (!user) return;
    const id = setInterval(fetchNotifs, 60000); // 60s poll
    return () => clearInterval(id);
  }, [user, fetchNotifs]);
  useEffect(() => {
    if (!showNotifs) return;
    const onClick = (e) => { if (notifMenuRef.current && !notifMenuRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showNotifs]);
  const handleNotifClick = async (n) => {
    try { await notificationsAPI.markRead([n.id]); } catch (_e) { /* noop */ }
    fetchNotifs();
    setShowNotifs(false);
    if (n.link) navigate(n.link);
  };
  const markAllRead = async () => {
    try { await notificationsAPI.markAllRead(); } catch (_e) { /* noop */ }
    fetchNotifs();
  };
  const fmtAge = (iso) => {
    if (!iso) return '';
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };
  const catLabel = {
    payment_success: 'Payment',
    paywall_alert: 'Paywall',
    credits_low: 'Credits low',
    bp_activity: 'Process',
    iam_activity: 'Team',
    security_alert: 'Security',
    welcome: 'Welcome',
  };
  const catColor = {
    payment_success: '#0F8A4F',
    paywall_alert: '#F26B1F',
    credits_low: '#F26B1F',
    bp_activity: '#1E4E8C',
    iam_activity: '#7C3AED',
    security_alert: '#DC2626',
    welcome: '#143A6B',
  };
  // Guard so the tour never auto-launches more than once per page session,
  // even if the user object is replaced by a later refreshUser() call.
  const tourAutoLaunchedRef = useRef(false);

  // After the welcome chat is dismissed, if the user hasn't completed the tour, launch it once.
  useEffect(() => {
    if (!user) return;
    if (tourAutoLaunchedRef.current) return;
    if (!showWelcome && !user.walkthroughCompleted && !showTour) {
      tourAutoLaunchedRef.current = true;
      const t = setTimeout(() => setShowTour(true), 400);
      return () => clearTimeout(t);
    }
  }, [showWelcome, user, showTour]);

  // Industry onboarding gate — redirect once if not yet selected
  const onboardingCheckedRef = useRef(false);
  useEffect(() => {
    if (!user || onboardingCheckedRef.current) return;
    if (window.location.pathname.startsWith('/onboarding')) return;
    onboardingCheckedRef.current = true;
    billingAPI.onboardingStatus().then((r) => {
      if (!r.data?.industrySelected) {
        navigate('/onboarding/industry');
      }
    }).catch(() => {});
  }, [user, navigate]);

  // Insufficient credits / AI exhausted — global modal
  const [creditModal, setCreditModal] = useState(null); // null | 'credits' | 'ai'
  useEffect(() => {
    const onCredits = () => setCreditModal('credits');
    const onAi = () => setCreditModal('ai');
    window.addEventListener('luminex:credits-exhausted', onCredits);
    window.addEventListener('luminex:ai-exhausted', onAi);
    return () => {
      window.removeEventListener('luminex:credits-exhausted', onCredits);
      window.removeEventListener('luminex:ai-exhausted', onAi);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    sessionStorage.removeItem('vidhana_welcome_dismissed');
    navigate('/login');
  };

  const relaunchTour = async () => {
    setHelpMenuOpen(false);
    try {
      await axios.post(`${API_URL}/api/users/me/walkthrough-reset`, {}, { headers: authHeader() });
    } catch { /* non-blocking */ }
    await refreshUser?.();
    setShowTour(true);
  };

  const reopenVidha = () => {
    setHelpMenuOpen(false);
    sessionStorage.removeItem('vidhana_welcome_dismissed');
    setShowWelcome(true);
  };

  const navItems = [
    { to: '/library', icon: <Books size={16} weight="duotone" />, label: 'Operating Models' },
    { to: '/folders', icon: <SquaresFour size={16} weight="duotone" />, label: 'Process Creation' },
    { to: '/reengineer', icon: <ArrowsClockwise size={16} weight="duotone" />, label: 'Process Re-engineering' },
    // Billing + Pricing are internal / operator-only surfaces — hide them from
    // regular tenant users so the nav stays focused on the BPM workflow.
    ...(user?.isSuperAdmin ? [
      { to: '/billing', icon: <CreditCard size={16} weight="duotone" />, label: 'Billing' },
      { to: '/pricing', icon: <CreditCard size={16} weight="duotone" />, label: 'Pricing' },
    ] : []),
    ...(user?.role === 'admin' ? [{ to: '/iam', icon: <UsersThree size={16} weight="duotone" />, label: 'Team' }] : []),
  ];

  // Live credit balance + L4 view quota (refreshes every 30s + on focus)
  const [creditBalance, setCreditBalance] = useState(null);
  const [viewQuota, setViewQuota] = useState(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchBoth = () => {
      billingAPI.balance().then(r => alive && setCreditBalance(r.data)).catch(() => {});
      // import lazily to avoid circular dep risk
      import('../store/authStore').then(({ lomAPI }) => {
        lomAPI.getViewQuota().then(r => alive && setViewQuota(r.data)).catch(() => {});
      });
    };
    fetchBoth();
    const id = setInterval(fetchBoth, 30000);
    const onFocus = () => fetchBoth();
    const onQuotaUpdate = () => fetchBoth();
    const onCreditUpdate = () => fetchBoth();
    window.addEventListener('focus', onFocus);
    window.addEventListener('l4-quota-updated', onQuotaUpdate);
    window.addEventListener('luminex:credit-balance-updated', onCreditUpdate);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); window.removeEventListener('l4-quota-updated', onQuotaUpdate); window.removeEventListener('luminex:credit-balance-updated', onCreditUpdate); };
  }, [user]);

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="app-layout">
      <header className="topbar">
        <div className="topbar-brand" data-testid="topbar-brand">
          <LuminexLockup iconSize={28} showDevanagari={true} onClick={() => navigate('/')} />
        </div>
        <div className="topbar-right">
          <GlobalSearchBar variant="compact" />
          <CompareHeaderBadge />
          {viewQuota && viewQuota.plan === 'free' && (
            <button
              data-testid="topbar-views-badge"
              onClick={() => navigate('/library')}
              title={`Free plan: ${viewQuota.viewedCount} of ${viewQuota.limit} L4 deep-dive views used. ${viewQuota.remaining ?? 0} remaining.`}
              style={{ background: viewQuota.blocked ? 'rgba(242,107,31,0.10)' : 'var(--bg-1)', border: `1px solid ${viewQuota.blocked ? 'var(--ember)' : 'var(--border-1)'}`, color: viewQuota.blocked ? 'var(--ember)' : 'var(--text-1)', cursor: 'pointer', padding: '6px 12px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700 }}
            >
              <Eye size={12} weight="duotone" />
              Views <strong style={{ fontWeight: 800 }}>{viewQuota.viewedCount}/{viewQuota.limit}</strong>
            </button>
          )}
          {viewQuota && viewQuota.plan !== 'free' && (
            <button
              data-testid="topbar-views-badge"
              onClick={() => navigate('/pricing')}
              title={`Paid plan: ${viewQuota.viewQuota ?? viewQuota.remaining ?? 0} L4 deep-dive views remaining. Buy more credits to top up.`}
              style={{ background: viewQuota.blocked ? 'rgba(242,107,31,0.10)' : 'var(--bg-1)', border: `1px solid ${viewQuota.blocked ? 'var(--ember)' : 'var(--border-1)'}`, color: viewQuota.blocked ? 'var(--ember)' : 'var(--text-1)', cursor: 'pointer', padding: '6px 12px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700 }}
            >
              <Eye size={12} weight="duotone" />
              Views <strong style={{ fontWeight: 800 }}>{viewQuota.viewQuota ?? viewQuota.remaining ?? 0}</strong>
            </button>
          )}
          <button
            className="token-badge"
            data-testid="credits-badge"
            onClick={() => navigate('/billing')}
            title={creditBalance ? `${(+creditBalance.credits).toFixed(Math.abs((+creditBalance.credits) % 1) > 0 ? 1 : 0)} credits · ${creditBalance.aiCalls} AI calls` : 'Loading credits…'}
            style={{ background: 'linear-gradient(135deg,var(--accent),var(--accent-hover))', color: 'var(--bg-1)', border: '1px solid var(--ember)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}
          >
            <Lightning size={14} weight="fill" style={{ color: 'var(--ember)' }} />
            Credits <strong style={{ fontWeight: 800 }}>{creditBalance ? (Math.abs((+creditBalance.credits) % 1) > 0 ? (+creditBalance.credits).toFixed(1) : (+creditBalance.credits).toFixed(0)) : '…'}</strong>
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => navigate('/pricing')} data-testid="buy-credits-btn">
            Buy Credits
          </button>

          {/* Help menu — relaunch tour or reopen Vidhā */}
          <div style={{ position: 'relative' }}>
            <button
              className="theme-toggle"
              onClick={() => setHelpMenuOpen(o => !o)}
              onBlur={() => setTimeout(() => setHelpMenuOpen(false), 150)}
              title="Help & guides"
              data-testid="help-btn"
            >
              <Question size={18} weight="duotone" />
            </button>
            {helpMenuOpen && (
              <div data-testid="help-menu" style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: 'var(--bg-1)', border: '1px solid var(--border-1)',
                borderRadius: 10, boxShadow: '0 12px 32px -8px rgba(10,14,26,0.3)',
                width: 240, padding: 6, zIndex: 60,
              }}>
                <button onMouseDown={relaunchTour} data-testid="help-relaunch-tour"
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 6, color: 'var(--text-1)' }}>
                  <Sparkle size={14} weight="duotone" />
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>Restart platform tour</div>
                </button>
                <button onMouseDown={() => { setHelpMenuOpen(false); navigate('/guide'); }} data-testid="help-open-guide"
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 6, color: 'var(--text-1)' }}>
                  <BookOpen size={14} weight="duotone" />
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>User guide & PDFs</div>
                </button>
                <button onMouseDown={reopenVidha} data-testid="help-open-vidha"
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 6, color: 'var(--text-1)' }}>
                  <Question size={14} weight="duotone" />
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>Chat with Vidhā</div>
                </button>
              </div>
            )}
          </div>

          <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`} data-testid="theme-toggle">
            {theme === 'light' ? <Moon size={18} weight="duotone" /> : <Sun size={18} weight="duotone" />}
          </button>

          {/* Notifications bell */}
          <div ref={notifMenuRef} style={{ position: 'relative' }}>
            <button
              className="theme-toggle"
              data-testid="notifications-bell"
              onClick={() => { setShowNotifs(s => !s); if (!showNotifs) fetchNotifs(); }}
              title={unreadCount ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'}
              style={{ position: 'relative' }}
            >
              <Bell size={18} weight="duotone" />
              {unreadCount > 0 && (
                <span data-testid="notifications-unread-badge" style={{ position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: 'var(--ember)', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--bg-0)', lineHeight: 1 }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifs && (
              <div data-testid="notifications-panel" style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: 'var(--bg-1)', border: '1px solid var(--border-1)',
                borderRadius: 12, boxShadow: '0 12px 32px -8px rgba(10,14,26,0.3)',
                width: 380, maxHeight: 480, padding: 0, zIndex: 60, overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong style={{ fontSize: 13, color: 'var(--text-1)' }}>Notifications</strong>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {unreadCount > 0 && (
                      <button data-testid="notifications-mark-all-read" onClick={markAllRead}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        Mark all read
                      </button>
                    )}
                    <button onClick={() => { setShowNotifs(false); navigate('/settings#notifications'); }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-3)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      Settings
                    </button>
                  </div>
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {notifs.length === 0 ? (
                    <div data-testid="notifications-empty" style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>
                      <Bell size={28} weight="duotone" style={{ color: 'var(--border-1)', marginBottom: 8 }} />
                      <div style={{ fontSize: 12 }}>You&apos;re all caught up.</div>
                    </div>
                  ) : (
                    notifs.map((n, i) => (
                      <button key={n.id} data-testid={`notification-item-${i}`}
                        onClick={() => handleNotifClick(n)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', background: n.read ? 'transparent' : 'rgba(30,78,140,0.04)', border: 'none', borderBottom: '1px solid var(--border-1)', cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 9, fontWeight: 800, color: catColor[n.category] || 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.10em' }}>
                            {catLabel[n.category] || n.category}
                          </span>
                          {!n.read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ember)' }} />}
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-3)' }}>{fmtAge(n.createdAt)}</span>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginBottom: 2 }}>{n.title}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.4 }}>{n.body}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <div ref={profileMenuRef} style={{ position: 'relative' }}>
            <button className="avatar-btn" title={user?.email} data-testid="user-avatar"
              onClick={() => setShowProfileMenu(v => !v)}>
              {getInitials()}
            </button>
            {showProfileMenu && (
              <div data-testid="profile-menu" style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: 'var(--bg-1)', border: '1px solid var(--border-1)',
                borderRadius: 10, boxShadow: '0 12px 32px -8px rgba(10,14,26,0.3)',
                width: 256, padding: 6, zIndex: 60,
              }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-1)', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-1)' }}>{((user?.firstName || '') + ' ' + (user?.lastName || '')).trim() || user?.email}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{user?.email}</div>
                </div>
                <button data-testid="profile-menu-settings" onMouseDown={() => { setShowProfileMenu(false); navigate('/settings'); }}
                  style={profileItemStyle}>
                  <Gear size={14} weight="duotone" /> Settings
                </button>
                <button data-testid="profile-menu-billing" onMouseDown={() => { setShowProfileMenu(false); navigate('/billing'); }}
                  style={profileItemStyle}>
                  <CreditCard size={14} weight="duotone" /> Orders & Invoices
                </button>
                <button data-testid="profile-menu-iam" onMouseDown={() => { setShowProfileMenu(false); navigate('/iam'); }}
                  style={profileItemStyle}>
                  <UsersThree size={14} weight="duotone" /> Team & Permissions
                </button>
                <div style={{ height: 1, background: 'var(--border-1)', margin: '4px 0' }} />
                {user?.isSuperAdmin && (
                  <button data-testid="profile-menu-admin-portal" onMouseDown={() => { setShowProfileMenu(false); navigate('/admin-portal'); }}
                    style={{ ...profileItemStyle, color: 'var(--ember)' }}>
                    <Gear size={14} weight="duotone" /> Admin Portal
                  </button>
                )}
                <button data-testid="profile-menu-logout" onMouseDown={async () => { setShowProfileMenu(false); await handleLogout(); }}
                  style={{ ...profileItemStyle, color: '#B0231C' }}>
                  <SignOut size={14} weight="duotone" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="nav-bar" data-testid="nav-bar">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => {
              // Deep-dive pages opened from Process Creation (`?from=folders`)
              // should keep the "Process Creation" tab lit, not "Operating
              // Models" (whose URL prefix `/library/*` they technically use).
              // Same for Re-engineering — `?reeng=true` on the deep-dive URL
              // means the user is iterating on an uploaded/optimized process
              // and Process Re-engineering should stay lit.
              const fromFolders = location.search.includes('from=folders');
              const inReeng     = location.search.includes('reeng=true');
              let effectiveActive = isActive;
              if (fromFolders) {
                if (item.to === '/library') effectiveActive = false;
                if (item.to === '/folders') effectiveActive = true;
              }
              if (inReeng) {
                if (item.to === '/library') effectiveActive = false;
                if (item.to === '/reengineer') effectiveActive = true;
              }
              return `nav-link ${effectiveActive ? 'active' : ''}`;
            }}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      <main className="app-main">
        <Outlet />
      </main>

      {/* Post-login concierge — every login, skip persists for the session */}
      {showWelcome && user && (
        <WelcomeChatOverlay user={user} onClose={() => setShowWelcome(false)} />
      )}

      {/* First-time walkthrough — once per user, re-launchable from Help menu */}
      {showTour && user && (
        <WalkthroughTour
          user={user}
          onClose={() => setShowTour(false)}
          onCompleted={() => refreshUser?.()}
        />
      )}

      {/* Insufficient credits / AI exhausted modal */}
      {creditModal && (
        <div data-testid={`${creditModal}-exhausted-modal`} onClick={() => setCreditModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(11,19,28,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 14, padding: 28, maxWidth: 440, width: '90%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              {creditModal === 'credits' ? 'Out of credits' : 'AI calls exhausted'}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)', margin: '0 0 8px' }}>
              {creditModal === 'credits' ? "You're out of credits" : 'AI calls used up on this process'}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '0 0 20px', lineHeight: 1.5 }}>
              {creditModal === 'credits'
                ? 'Buy a credit pack to keep creating processes. Credits never expire and each one bundles 8 AI calls.'
                : 'Buy more credits to top up — manual editing, exports and versioning still work as normal on this process.'}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setCreditModal(null)} style={{ flex: 1, padding: '11px 14px', borderRadius: 8, border: '1px solid var(--border-1)', background: 'white', color: 'var(--accent)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Maybe later</button>
              <button data-testid="modal-buy-credits" onClick={() => { setCreditModal(null); navigate('/pricing'); }} style={{ flex: 1, padding: '11px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: 'var(--bg-1)', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>Buy credits</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Compare tray header badge ─────────────────────────────────────────
// Small pill that shows the current Compare-tray count and jumps to
// /compare on click. Hidden when the tray is empty so the header stays
// clean until the user actually stages something.
function CompareHeaderBadge() {
  const items = useCompareStore(s => s.items);
  const navigate = useNavigate();
  if (!items || items.length === 0) return null;
  return (
    <button
      data-testid="header-compare-badge"
      onClick={() => navigate('/compare')}
      title="Open Cross-LOM Compare tray"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 999,
        background: 'var(--accent-dim)', border: '1px solid var(--accent)',
        color: 'var(--accent)', fontSize: 11, fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      <GitCompareArrows size={12} /> Compare
      <span data-testid="header-compare-count" style={{ padding: '1px 6px', borderRadius: 8, background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 10 }}>
        {items.length}
      </span>
    </button>
  );
}
